import { useTranslation } from 'react-i18next';
import type {
  ConsentOverviewColumn,
  ConsentOverviewRow,
} from '@/api/consents/consentsApi.types';
import { Badge, type BadgeTone } from '@/components/ui/badge';
import { Card } from '@/components/ui/card';
import { Icon } from '@/components/common/Icon';
import { buildNameInitials } from '@/lib/nameInitials';
import { ConsentStatusPill } from './ConsentStatusPill';

interface ConsentOverviewTableProps {
  columns: ConsentOverviewColumn[];
  rows: ConsentOverviewRow[];
  hasFilters: boolean;
  onOpenClient: (clientId: string) => void;
}

export function ConsentOverviewTable({
  columns,
  rows,
  hasFilters,
  onOpenClient,
}: ConsentOverviewTableProps) {
  const { t } = useTranslation('consents');

  if (rows.length === 0) {
    return (
      <Card padding="lg" className="text-center text-sm text-zoomuted">
        {hasFilters === true ? t('overview.emptyFiltered') : t('overview.empty')}
      </Card>
    );
  }

  return (
    <Card padding="none" className="overflow-x-auto">
      <table className="w-full text-left">
        <thead>
          <tr className="border-b border-zooborder">
            <HeaderCell>{t('overview.column.client')}</HeaderCell>
            {columns.map(function renderColumnHead(column) {
              return <HeaderCell key={column.documentId}>{column.title}</HeaderCell>;
            })}
            <HeaderCell className="text-right">{t('overview.column.status')}</HeaderCell>
          </tr>
        </thead>
        <tbody className="divide-y divide-zooborder">
          {rows.map(function renderRow(row) {
            return (
              <tr
                key={row.clientId}
                onClick={function open() {
                  onOpenClient(row.clientId);
                }}
                className="cursor-pointer hover:bg-zoovpalelavender hover:bg-opacity-30 transition-colors"
              >
                <td className="px-4 sm:px-5 py-3.5">
                  <div className="flex items-center gap-3">
                    <span className="w-9 h-9 rounded-full bg-zoovpalelavender text-zooprimary flex items-center justify-center font-bold text-xs shrink-0">
                      {buildNameInitials(row.clientDisplayName)}
                    </span>
                    <span className="text-sm font-bold text-zooindigo truncate">
                      {row.clientDisplayName}
                    </span>
                  </div>
                </td>
                {columns.map(function renderCell(column) {
                  const cell = row.cells.find(function matches(candidate) {
                    return candidate.documentId === column.documentId;
                  });
                  return (
                    <td key={column.documentId} className="px-4 py-3.5">
                      {cell === undefined ? (
                        <span className="text-zoomuted">—</span>
                      ) : (
                        <ConsentStatusPill cell={cell} />
                      )}
                    </td>
                  );
                })}
                <td className="px-4 sm:px-5 py-3.5 text-right">
                  <div className="inline-flex items-center gap-2">
                    <RowSummaryBadge row={row} />
                    <Icon name="ChevronRight" className="text-zoomuted inline-block" />
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </Card>
  );
}

function RowSummaryBadge({ row }: { row: ConsentOverviewRow }) {
  const { t } = useTranslation('consents');
  let tone: BadgeTone = 'success';
  let labelKey = 'overview.summary.complete';
  if (row.hasReacceptance === true) {
    tone = 'warning';
    labelKey = 'overview.summary.reacceptance';
  } else if (row.hasPending === true) {
    tone = 'muted';
    labelKey = 'overview.summary.pending';
  }
  return (
    <Badge tone={tone} shape="tag" size="sm" uppercase className="text-[10px] hidden sm:inline-flex">
      {t(labelKey)}
    </Badge>
  );
}

function HeaderCell({ children, className }: { children: React.ReactNode; className?: string }) {
  return (
    <th
      scope="col"
      className={
        'px-4 sm:px-5 py-3 text-[10.5px] font-bold uppercase tracking-wider text-zoomuted ' +
        (className === undefined ? '' : className)
      }
    >
      {children}
    </th>
  );
}
